import * as deliverynoteApi from 'client/api/deliverynote'
import * as documentApi from 'client/api/document'
import { getReceipt } from 'client/api/receipt'
import { DocumentType, DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { InvoiceInterface } from 'client/Interfaces/Invoice/InvoiceInterface'
import { TradeNameInterface } from 'client/Interfaces/Settings/TradeNameInterface'
import { StateInterface } from 'client/Interfaces/StateInterface'
import {
  constructDeliverynoteFileName,
  constructReceiptFileName,
  consturctConvertedDocumentFileName,
} from 'client/Other/functions/documentFunctions'
import { settingsSelector } from 'client/reducers/settings'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

const delaySec = (s) => new Promise((resolve) => setTimeout(resolve, s * 1000))

export const generatePdfForDownLoad = async (fileName: string, pdfData: string) => {
  // Wait for a few seconds so that the PDF is ready
  await delaySec(3)
  const link = document.createElement('a')
  const blob = new Blob([pdfData], { type: 'application/pdf' })
  link.href = window.URL.createObjectURL(blob)
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const downloadPdfFromInvoice = async (
  type: DocumentType,
  id: number,
  token: StateInterface['token'],
  translations,
  tradeNames: TradeNameInterface[],
  invoiceNumber?: string,
) => {
  try {
    const tradeName = tradeNames[0]
    const pdfData = await documentApi.getConvertedDocumentPDFV1(type, id)
    const invoice = (await documentApi.getDocument(DocumentTypes.INVOICE, id)) as InvoiceInterface
    const typeName = translations[invoice.invoicedocument.fields.locale.value]
    const invoiceNum = invoiceNumber ? `${invoiceNumber} ` : ''
    const fileName = consturctConvertedDocumentFileName(typeName, tradeName, invoiceNum)

    await generatePdfForDownLoad(fileName, pdfData)
  } catch (e) {
    console.error('Failed to download PDF', e)
  }
}

export const downloadPdfByIdV1 = async (
  type: DocumentType,
  id: number,
  token: StateInterface['token'],
  tradeNames: TradeNameInterface[],
): Promise<void> => {
  let fileName: string
  let pdfData
  try {
    if (type === DocumentTypes.DELIVERYNOTE) {
      const deliverynote = await deliverynoteApi.getDeliverynote(id)
      pdfData = await documentApi.getDocumentPDFV1(type, id)
      const tradeName = tradeNames.find((t) => t._id === deliverynote?.company?.tradeName._id) || tradeNames[0]
      fileName = constructDeliverynoteFileName(deliverynote, tradeName)
    } else if (type === DocumentTypes.RECEIPT) {
      const receipt = await getReceipt(id)
      pdfData = await documentApi?.getDocumentPDFV1(type, id)
      const tradeName = tradeNames.find((t) => t._id === receipt?.company?.tradeName._id)
      fileName = constructReceiptFileName(receipt, tradeName)
    }
    await generatePdfForDownLoad(fileName, pdfData)
  } catch (e) {
    console.error('Failed to download PDF', e)
  }
}

export const useDownloadPdf = (type: DocumentType, id: number): (() => Promise<void>) => {
  const { token, tradeNames } = useSelector((s: StateInterface) => ({
    token: s.token,
    tradeNames: settingsSelector(s).values.company.tradeNames,
  }))
  return useCallback(async () => {
    await downloadPdfByIdV1(type, id, token, tradeNames)
  }, [id, token, tradeNames, type])
}
